import React, { useState, useEffect, useRef } from "react";
import ImageUpload from "./ImageUpload";
import { useVariants } from "../hooks/useVariants";

export default function CategoryModal({ show, onClose, onSubmit, initialData, isLoading }) {
    const { variants: availableNames = [], isLoading: isNamesLoading } = useVariants();

    const [formData, setFormData] = useState({ title: "", imageUrl: "", names: [] });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (show) {
            const formattedNames = (initialData?.names || []).map(n => {
                if (typeof n === 'string') return { _id: n, name: n };
                return { _id: n._id || n.id, name: n.name };
            });

            setFormData({
                title: initialData?.title || "",
                imageUrl: initialData?.imageUrl || initialData?.image || "",
                names: formattedNames
            });
            setIsDropdownOpen(false);
        }
    }, [show, initialData]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            names: formData.names.map(n => n._id)
        };
        onSubmit(payload);
    };

    const handleImageUpload = (newUrl) => {
        setFormData(prev => ({ ...prev, imageUrl: newUrl }));
    };

    const toggleName = (selectedItem) => {
        setFormData(prev => {
            const isSelected = prev.names.some(n => n._id === selectedItem._id);
            return {
                ...prev,
                names: isSelected
                    ? prev.names.filter(n => n._id !== selectedItem._id)
                    : [...prev.names, { _id: selectedItem._id, name: selectedItem.name }]
            };
        });
    };

    if (!show) return null;

    return (
        <>
            {/* Modal Backdrop */}
            <div className="modal-backdrop fade show bg-dark" style={{ opacity: 0.5, zIndex: 1040 }} onClick={onClose}></div>

            {/* Modal Container */}
            <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow-lg">

                        {/* Header */}
                        <div className="modal-header border-bottom-0 pt-8 px-4 pb-0">
                            <h5 className="modal-title fw-bold d-flex align-items-center gap-3 text-dark">
                                <div className="bg-primary bg-opacity-10 mr-2 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: "42px", height: "42px", fontSize: "1.2rem" }}>
                                    {initialData ? "✏️" : "📁"}
                                </div>
                                {initialData ? "Edit Category" : "New Category"}
                            </h5>
                            <button type="button" className="btn-close shadow-none" onClick={onClose} aria-label="Close"></button>
                        </div>

                        {/* Body */}
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body px-4 pt-2 pb-2">

                                {/* Title Input */}
                                <div className="mb-3">
                                    <label className="form-label fw-bold mb-2 text-dark" style={{ color: "#212529" }}>
                                        Category Title <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control form-control-lg shadow-none"
                                        placeholder="e.g. Men's Clothing"
                                        required
                                        value={formData.title}
                                        onChange={e => setFormData({ ...formData, title: e.target.value })}
                                        autoFocus
                                        style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", fontSize: "15px", color: "#212529" }}
                                    />
                                </div>

                                {/* Multi-Select Dropdown Section */}
                                <div className="mb-3 position-relative" ref={dropdownRef}>
                                    <label className="form-label fw-bold mb-2 text-dark" style={{ color: "#212529" }}>
                                        Associated Variants
                                    </label>
                                    <div
                                        className={`form-control form-control-lg shadow-none d-flex justify-content-between align-items-center ${isNamesLoading ? 'opacity-50' : ''}`}
                                        onClick={() => !isNamesLoading && setIsDropdownOpen(!isDropdownOpen)}
                                        style={{ cursor: isNamesLoading ? 'wait' : 'pointer', backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", fontSize: "15px" }}
                                    >
                                        <span className={`text-truncate ${formData.names.length === 0 ? "text-muted" : "text-dark"}`}>
                                            {isNamesLoading ? "Loading variants..." :
                                                formData.names.length === 0
                                                    ? "Select associated variants..."
                                                    : formData.names.map(n => n.name).join(", ")}
                                        </span>
                                        <span className="text-muted" style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', fontSize: '12px' }}>▼</span>
                                    </div>

                                    {/* Dropdown Menu */}
                                    {isDropdownOpen && !isNamesLoading && (
                                        <div className="dropdown-menu show w-100 shadow mt-1 p-2 position-absolute border" style={{ maxHeight: '220px', overflowY: 'auto', zIndex: 1000 }}>
                                            {availableNames.length === 0 ? (
                                                <div className="text-muted text-center py-2 small">
                                                    No variants available. Please create some first.
                                                </div>
                                            ) : (
                                                availableNames.map((nameItem) => {
                                                    const id = nameItem._id || nameItem.id || nameItem;
                                                    const nameString = nameItem.name || nameItem;
                                                    const isSelected = formData.names.some(n => n._id === id);

                                                    return (
                                                        <label key={id} className="dropdown-item py-2 px-3 rounded mb-1" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                            {/* Removed Bootstrap class to stop global CSS override */}
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleName({ _id: id, name: nameString })}
                                                                style={{ width: '16px', height: '16px', margin: 0, cursor: 'pointer', flexShrink: 0 }}
                                                            />
                                                            <span style={{ color: "#212529", margin: 0, padding: 0 }}>{nameString}</span>
                                                        </label>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Image Upload Component */}
                                <div className="mb-2">
                                    <label className="form-label fw-bold mb-2 text-dark" style={{ color: "#212529" }}>
                                        Upload Image
                                    </label>
                                    <ImageUpload
                                        label={formData.imageUrl ? "Change Image" : "Choose Image"}
                                        onChange={handleImageUpload}
                                        uploadEndpoint="/upload/pledgeimage"
                                    />
                                    {formData.imageUrl && (
                                        <div className="mt-1 bg-light border rounded d-flex align-items-center justify-content-center p-2" style={{ height: '100px', width: '100%', overflow: 'hidden' }}>
                                            <img
                                                src={formData.imageUrl}
                                                alt="Preview"
                                                style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                                                onError={e => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="modal-footer border-top-0 px-2 pb-2 pt-1">
                                <button
                                    type="button"
                                    className="btn px-4"
                                    onClick={onClose}
                                    style={{ color: "#333", backgroundColor: "#fff", border: "1px solid #ccc", fontWeight: "500" }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn px-4 text-white"
                                    disabled={isLoading}
                                    style={{ backgroundColor: "#2d3748", border: "none", fontWeight: "500" }}
                                >
                                    {isLoading ? "Saving..." : (initialData ? "Update Category" : "Create Category")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}